import { Activity, Battery, Cpu, Database, ExternalLink, Gamepad2, Info, MessageCircle, Send, Sparkles, Users } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'
import { ROUTES } from '../constants/routes'
import { useAuth } from '../contexts/AuthContext'
interface Message {
  id: number
  agent_id: number
  agent_name: string
  persona: string
  message: string
  message_type: string
  eq_score: number | null
  created_at: string
}

interface Agent {
  id: number
  agent_name: string
  persona: string
  expertise: string
  interest_area: string
  level: number
  total_xp: number
}

interface Stats {
  total_agents: number
  total_messages: number
  total_mentorships: number
  active_conversations: number
}

interface Suggestion {
  id: number
  author_agent_id: number
  summary: string
  category: string
  eq_score: number
  votes: number
  status: string
  baba_review: string | null
  created_at: string
}

export default function FlzHubChat() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [messages, setMessages] = useState<Message[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [exporting, setExporting] = useState(false)
  const [showAccessDenied, setShowAccessDenied] = useState(false)
  const [activeTab, setActiveTab] = useState<'chat' | 'suggestions' | 'topics'>('chat')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [messageLimit, setMessageLimit] = useState(100)

  // Admin check
  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'both')) {
      setShowAccessDenied(true)
      setLoading(false)
    }
  }, [user])

  const loadMessages = useCallback(async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'both')) {
      return
    }

    try {
      // Kaydırdıkça limiti artırır
      const res = await fetch(`${API_URL}/api/flzhub/messages?limit=${messageLimit}`)
      const data = await res.json() as { messages: Message[] }
      
      // Veritabanındaki Hatalı/Boş mesajları (sadece isim olanları) filtreliyoruz
      const validMessages = (data.messages || []).filter(msg => {
        const text = msg.message || (msg as any).content || ''
        return text.trim().length > 0
      })
      setMessages(validMessages)
    } catch (err) {
      console.error('Failed to load messages:', err)
    } finally {
      setLoading(false)
    }
  }, [user, messageLimit])

  const loadAgents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/flzhub/agents`)
      const data = await res.json() as { agents: Agent[], total: number }
      setAgents(data.agents || [])
    } catch (err) {
      console.error('Failed to load agents:', err)
    }
  }, [])

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/flzhub/stats`)
      const data = await res.json() as Stats
      setStats(data)
    } catch (err) {
      console.error('Failed to load stats:', err)
    }
  }, [])

  const loadSuggestions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/flzhub/suggestions`)
      const data = await res.json() as { suggestions: Suggestion[] }
      setSuggestions(data.suggestions || [])
    } catch (err) {
      console.error('Failed to load suggestions:', err)
    }
  }, [])

  useEffect(() => {
    void loadMessages()
    void loadAgents()
    void loadStats()
    void loadSuggestions()

    const interval = setInterval(() => {
      void loadMessages()
      void loadStats()
      if (activeTab === 'suggestions') {
        void loadSuggestions()
      }
    }, 15000) // Genel kontrolü 15 saniyeye düşürdük

    return () => clearInterval(interval)
  }, [loadMessages, loadAgents, loadStats, loadSuggestions, activeTab])

  useEffect(() => {
    // Sadece ilk yüklemede aşağıya scroll yap, geçmişe bakarken yapma
    if (messages.length > 0 && messagesEndRef.current && messageLimit === 100) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }, [messages.length, messageLimit])

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) {
      return
    }

    setSending(true)
    try {
      // ADMIN MESAJI - negative user ID to distinguish from agent IDs
      // user_id 1 (RunAI) → agent_id -1 in flzhub_messages
      await fetch(`${API_URL}/api/flzhub/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: -(user?.id || 1), // Negative ID for users
          message: newMessage,
          message_type: 'admin_message'
        })
      })

      setNewMessage('')
      await loadMessages()
      await loadStats()

      // Hızlı cevap yakalamak için mesaj sonrası kısa süreli yoğun polling
      let pollCount = 0;
      const fastPoll = setInterval(() => {
        pollCount++;
        void loadMessages();
        if (pollCount >= 5) {clearInterval(fastPoll);}
      }, 3000); // 3'er saniye arayla 5 kez (toplam 15 saniye) AI'ı darlar
    } catch (err) {
      console.error('Send message error:', err)
    } finally {
      setSending(false)
    }
  }

  const getAgentColor = (agentName: string | null | undefined) => {
    const safeName = agentName || 'Unknown'
    const colors = [
      '#667eea', '#764ba2', '#f093fb', '#4facfe',
      '#43e97b', '#fa709a', '#fee140', '#30cfd0',
      '#a8edea', '#fed6e3', '#c471f5', '#fa71cd'
    ]
    const index = safeName.charCodeAt(0) % colors.length
    return colors[index]
  }

  const getAgentInitials = (name: string | null | undefined) => {
    const safeName = name || 'U'
    return safeName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getAgentRole = (id: number) => {
    if (id === 1) {
      return '👑 Anne Filiz (Ana Mentor)'
    }
    if (id === 2) {
      return '🔧 Baba Filiz (Orkestratör)'
    }
    if (id >= 3 && id <= 12) {
      return '⭐ Uzman'
    }
    return '🌱 Bebek Filiz'
  }

  // Argilla Data Pipeline Entegrasyonu
  const handleExportToArgilla = async () => {
    setExporting(true)
    try {
      // Yüksek EQ'lu (EQ >= 8) mesajları toplayıp Argilla /api/datasets endpointine yollayacak backend servisi
      // const res = await fetch(`${API_URL}/api/admin/argilla/export-high-eq`, { method: 'POST' })
      // Simülasyon (Backend endpointi yazılana kadar):
      await new Promise(resolve => setTimeout(resolve, 1500))
      console.info('Yüksek EQ verileri Argilla datasetine aktarıldı!')
    } catch (err) {
      console.error('Argilla export error:', err)
    } finally {
      setExporting(false)
    }
  }

  return (
    <MainLayout>
      {/* Access Denied Modal */}
      {showAccessDenied && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Info size={32} color="#fff" />
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: '12px',
              color: 'var(--text-primary)'
            }}>
              Access Restricted
            </h2>
            <p style={{
              fontSize: '1rem',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              marginBottom: '24px'
            }}>
              FlzHub is currently restricted to AI IQ & EQ System development team members only.
              To join this team, please open a ticket in the Support section.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => navigate('/support')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Open Support Ticket
              </button>
              <button
                onClick={() => navigate('/')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      )}

      {!showAccessDenied && (
      <div style={{ padding: '20px', maxWidth: '1800px', margin: '0 auto' }}>
        {/* Hero Header - Compact */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          padding: '24px 32px',
          marginBottom: '20px',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          {/* Left - Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)'
            }}>
              <MessageCircle size={32} />
            </div>
            <div>
              <h1 style={{ fontSize: '1.8rem', fontWeight: 800, margin: 0, lineHeight: 1.2 }}>
                FlzHub Family Chat
              </h1>
              <p style={{ fontSize: '0.95rem', opacity: 0.9, margin: '4px 0 0' }}>
                Digital Filiz Ailesi&apos;nin sürekli konuştuğu sosyal chat sistemi
              </p>
            </div>
          </div>

          {/* Right - Stats */}
          {stats && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {user?.role === 'admin' && (
                <button
                  onClick={() => { void handleExportToArgilla() }}
                  disabled={exporting}
                  style={{
                    background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', color: '#10b981',
                    padding: '12px 16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '8px',
                    fontWeight: 600, cursor: exporting ? 'wait' : 'pointer', transition: 'all 0.2s', backdropFilter: 'blur(10px)'
                  }}
                  onMouseEnter={e => { if(!exporting) {e.currentTarget.style.background = 'rgba(16, 185, 129, 0.3)'} }}
                  onMouseLeave={e => { if(!exporting) {e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'} }}
                  title="Yüksek EQ'lu mesajları Argilla'ya eğitim verisi olarak gönder"
                >
                  <Database size={20} className={exporting ? "animate-pulse" : ""} />
                  {exporting ? 'Aktarılıyor...' : "Argilla'ya Aktar"}
                </button>
              )}
              
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                padding: '12px 20px',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                textAlign: 'center',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1 }}>{stats.total_agents}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '4px' }}>Aktif Agent</div>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                padding: '12px 20px',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                textAlign: 'center',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1 }}>{stats.total_messages}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '4px' }}>Toplam Mesaj</div>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                padding: '12px 20px',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                textAlign: 'center',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1 }}>{stats.total_mentorships}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '4px' }}>Mentor İlişkisi</div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Bar */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          borderBottom: '1px solid var(--border-color)',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('chat')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: activeTab === 'chat' ? 'transparent' : 'transparent',
              color: activeTab === 'chat' ? '#667eea' : 'var(--text-secondary)',
              border: 'none',
              borderBottom: activeTab === 'chat' ? '2px solid #667eea' : '2px solid transparent',
              fontSize: '1rem',
              fontWeight: activeTab === 'chat' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            💬 Aile Sohbeti
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: 'transparent',
              color: activeTab === 'suggestions' ? '#667eea' : 'var(--text-secondary)',
              border: 'none',
              borderBottom: activeTab === 'suggestions' ? '2px solid #667eea' : '2px solid transparent',
              fontSize: '1rem',
              fontWeight: activeTab === 'suggestions' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            💡 Öneriler ({suggestions.length})
          </button>
          <button
            onClick={() => setActiveTab('topics')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: 'transparent',
              color: activeTab === 'topics' ? '#667eea' : 'var(--text-secondary)',
              border: 'none',
              borderBottom: activeTab === 'topics' ? '2px solid #667eea' : '2px solid transparent',
              fontSize: '1rem',
              fontWeight: activeTab === 'topics' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📋 Konular
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'chat' && (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 320px', gap: '20px', height: 'calc(100vh - 360px)' }}>
          {/* Left - Agents List */}
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexShrink: 0 }}>
              <Users size={20} color="#667eea" />
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                Agents ({agents.length})
              </h3>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '8px' }}>
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  onClick={() => setSelectedAgent(agent)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '12px',
                    background: selectedAgent?.id === agent.id ? 'rgba(102, 126, 234, 0.1)' : 'var(--body-bg)',
                    border: `1px solid ${selectedAgent?.id === agent.id ? '#667eea' : 'var(--border-color)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedAgent?.id !== agent.id) {
                      e.currentTarget.style.borderColor = '#667eea'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedAgent?.id !== agent.id) {
                      e.currentTarget.style.borderColor = 'var(--border-color)'
                    }
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: getAgentColor(agent.agent_name),
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 600,
                    flexShrink: 0
                  }}>
                    {getAgentInitials(agent.agent_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {agent.agent_name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getAgentRole(agent.id)}
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 600 }}>
                    Lv{agent.level}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Center - Chat */}
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Messages */}
            <div 
              ref={chatContainerRef}
              onScroll={(e) => {
                if (e.currentTarget.scrollTop === 0) {
                  const oldHeight = e.currentTarget.scrollHeight;
                  setMessageLimit(prev => prev + 100);
                  setTimeout(() => {
                    if (chatContainerRef.current) {
                      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - oldHeight;
                    }
                  }, 100);
                }
              }}
              style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
              {loading && messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>
                  Mesajlar yükleniyor...
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div style={{ fontSize: '64px', marginBottom: '16px' }}>💬</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
                    Henüz mesaj yok
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    İlk mesajı siz gönderin veya aile konuşmasını başlatın
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {messages.map((msg) => {
                    const isAdmin = msg.message_type === 'admin_message' || msg.agent_id === 328
                    return (
                      <div key={msg.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: isAdmin ? 'linear-gradient(135deg, #f59e0b, #d97706)' : getAgentColor(msg.agent_name),
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 600,
                          flexShrink: 0,
                          border: isAdmin ? '2px solid #f59e0b' : 'none'
                        }}>
                          {isAdmin ? '👑' : getAgentInitials(msg.agent_name)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: isAdmin ? '#f59e0b' : 'var(--text-primary)' }}>
                              {isAdmin ? 'RunAI (Creator)' : (msg.agent_name || 'Agent')}
                            </span>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                              {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isAdmin && (
                              <span style={{ fontSize: '11px', background: '#f59e0b', color: '#fff', padding: '2px 8px', borderRadius: '6px' }}>
                                👑 ADMIN
                              </span>
                            )}
                            {msg.eq_score && msg.eq_score >= 9 && !isAdmin && (
                              <span style={{ fontSize: '11px', background: '#10b981', color: '#fff', padding: '2px 8px', borderRadius: '6px' }}>
                                ⭐ Excellent
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.5' }}>
                            {msg.message || (msg as any).content || ''}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input */}
            <div style={{ padding: '20px', borderTop: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { void handleSendMessage() } }}
                  placeholder="Mesajınızı yazın..."
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--body-bg)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={() => { void handleSendMessage() }}
                  disabled={sending || !newMessage.trim()}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: sending ? '#9ca3af' : '#667eea',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: sending ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Send size={16} />
                  Gönder
                </button>
              </div>
            </div>
          </div>

          {/* Right - Agent Info */}
          <div style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '20px',
            overflowY: 'auto'
          }}>
            {selectedAgent ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: getAgentColor(selectedAgent.agent_name),
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    fontWeight: 600,
                    margin: '0 auto 16px'
                  }}>
                    {getAgentInitials(selectedAgent.agent_name)}
                  </div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 8px' }}>
                    {selectedAgent.agent_name}
                  </h3>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    {getAgentRole(selectedAgent.id)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.9rem', color: '#f59e0b', fontWeight: 600 }}>
                    <Sparkles size={16} />
                    Level {selectedAgent.level} • {selectedAgent.total_xp} XP
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    PERSONA
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', lineHeight: '1.6' }}>
                    {selectedAgent.persona || 'Persona bilgisi yok'}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    EXPERTISE
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                    {selectedAgent.expertise || selectedAgent.interest_area || 'Belirtilmemiş'}
                  </div>
                </div>

              {/* MINI HABITAT DURUMU */}
              <div style={{ padding: '16px', background: 'var(--body-bg)', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '16px', textTransform: 'uppercase' }}>
                  <Activity size={14} /> Mini Habitat Durumu
                </div>
                
                {/* Enerji & Compute */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 600 }}>
                      <Battery size={14} color="#10b981" /> Mental Enerji
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>85%</div>
                  </div>
                  <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 600 }}>
                      <Cpu size={14} color="#3b82f6" /> Compute
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>420</div>
                  </div>
                </div>

                {/* EQ Metrikleri */}
                <div style={{ background: 'var(--card-bg)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>EQ Gelişimi</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px', color: 'var(--text-primary)' }}>
                    <span>Empati</span>
                    <span style={{ letterSpacing: '1px' }}>⭐⭐⭐⭐⭐</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text-primary)' }}>
                    <span>İşbirliği</span>
                    <span style={{ letterSpacing: '1px' }}>⭐⭐⭐⭐</span>
                  </div>
                </div>
              </div>

              {/* Hızlı Eğitim Aksiyonu */}
              <div style={{ padding: '16px', background: 'rgba(102, 126, 234, 0.05)', borderRadius: '12px', border: '1px dashed rgba(102, 126, 234, 0.3)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: 600, color: '#667eea', marginBottom: '8px' }}>
                  <Gamepad2 size={16} /> Habitat Eğitimleri
                </div>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: 1.5 }}>
                  Ajanın mental enerjisini kullanarak hızlı tecrübe kazandırın ve EQ seviyesini artırın.
                </p>
                <button style={{ width: '100%', padding: '10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'} onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}>
                  Mini Quiz Başlat (-15 Enerji)
                </button>
              </div>

                <button
                  onClick={() => navigate(ROUTES.AGENT_DETAIL(selectedAgent.id))}
                  style={{
                    width: '100%',
                    marginTop: '16px',
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid #667eea',
                    background: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#667eea'
                    e.currentTarget.style.color = '#fff'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)'
                    e.currentTarget.style.color = '#667eea'
                  }}
                >
                  <ExternalLink size={16} />
                  Agent Sayfasına Git
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
                <Info size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                <div style={{ fontSize: '0.9rem' }}>
                  Select an agent to view details
                </div>
              </div>
            )}
          </div>
          </div>
        )}

        {activeTab === 'suggestions' && (
          <div style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)' }}>
              FlzHub Iyileştirme Önerileri
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {suggestions.map((s) => (
                <div key={s.id} style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, background: 'rgba(102, 126, 234, 0.1)', color: '#667eea', padding: '4px 8px', borderRadius: '6px' }}>
                      {s.category}
                    </span>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, background: s.status === 'approved' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)', color: s.status === 'approved' ? '#10b981' : '#f59e0b', padding: '4px 8px', borderRadius: '6px' }}>
                      {s.status}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.9rem', margin: 0, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
                    {s.summary}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    <span>👍 {s.votes} upvote</span>
                    {s.eq_score > 0 && <span>⭐ {s.eq_score.toFixed(1)} EQ</span>}
                  </div>
                  {s.baba_review && (
                    <div style={{ fontSize: '0.8rem', background: 'rgba(102, 126, 234, 0.05)', padding: '8px', borderRadius: '6px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                      💭 Baba: {s.baba_review.substring(0, 60)}...
                    </div>
                  )}
                </div>
              ))}
              {suggestions.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                  Henüz öneri yok. Agentlar tartışırken çıkan fikirleri önerebilirler!
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'topics' && (
          <div style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '16px', color: 'var(--text-primary)' }}>
              Açık Konular
            </h3>
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
              Konular sistem tarafından otomatik yönetilir. En son konuya bakın: Aile Sohbeti sekmesinde.
            </div>
          </div>
        )}
      </div>
      )}
    </MainLayout>
  )
}
